import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Swords, MapPin, Flame, Check, X, Loader2, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIMES = ["6am","7am","8am","9am","10am","11am","12pm","1pm","2pm","3pm","4pm","5pm","6pm","7pm","8pm","9pm"];

export default function Play() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("singles");
  const [dialog, setDialog] = useState(null);
  const [courts, setCourts] = useState([]);
  const [courtId, setCourtId] = useState("");
  const [msg, setMsg] = useState("");
  const [partnerId, setPartnerId] = useState("");
  const [oppPartnerId, setOppPartnerId] = useState("");
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, cts] = await Promise.all([
        http.get("/matchmaking/suggestions"),
        http.get("/challenges"),
        http.get("/courts"),
      ]);
      setPlayers(p.data);
      setChallenges(c.data);
      setCourts(cts.data);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openChallenge = (p) => { setDialog(p); setMsg("Let's rally — winner gets bragging rights."); setCourtId(""); setPartnerId(""); setOppPartnerId(""); setDay(""); setTime(""); };

  const send = async () => {
    if (mode === "doubles" && (!partnerId || !oppPartnerId)) {
      toast.error("Pick your partner and your opponents' partner to lock in all four players.");
      return;
    }
    const proposedTime = day && time ? `${day} ${time}` : null;
    try {
      await http.post("/challenges", {
        opponent_id: dialog.user_id,
        match_type: mode,
        court_id: courtId || null,
        message: msg,
        proposed_time: proposedTime,
        partner_id: mode === "doubles" ? partnerId : null,
        opponent_partner_id: mode === "doubles" ? oppPartnerId : null,
      });
      toast.success(`Challenge sent to ${dialog.name}. Ball is in their court.`);
      setDialog(null);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const respond = async (id, action) => {
    try {
      await http.post(`/challenges/${id}/respond`, null, { params: { action } });
      toast.success(action === "accept" ? "Challenge accepted — game on." : "Challenge declined.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const incoming = challenges.filter((c) => c.opponent_id === user?.user_id && c.status === "pending");
  const outgoing = challenges.filter((c) => c.challenger_id === user?.user_id);

  return (
    <div className="space-y-6" data-testid="play-page">
      <section className="hero-glow rounded-2xl p-6 sm:p-8 text-white">
        <div className="pill pv-bg-primary text-black mb-3">MATCHMAKING ARCADE</div>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold leading-tight">
          Find your rival. <span style={{color:"#BED445"}}>Set the score.</span>
        </h1>
        <p className="text-white/80 mt-2 max-w-xl">Skill-matched players near you. Send a challenge, lock a court, and let the paddles do the talking.</p>
        <div className="mt-5 inline-flex items-center gap-2 p-1 rounded-full" style={{background:"rgba(255,255,255,.08)"}}>
          {["singles","doubles"].map(m => (
            <button key={m} data-testid={`mode-${m}-btn`} onClick={()=>setMode(m)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold capitalize transition-all ${mode===m?"pv-bg-primary text-black":"text-white/80"}`}>
              {m}
            </button>
          ))}
        </div>
      </section>

      <Tabs defaultValue="suggested" className="w-full">
        <TabsList className="pv-bg-dark text-white rounded-full p-1">
          <TabsTrigger value="suggested" data-testid="tab-suggested" className="data-[state=active]:pv-bg-primary data-[state=active]:text-black rounded-full">Suggested</TabsTrigger>
          <TabsTrigger value="incoming" data-testid="tab-incoming" className="data-[state=active]:pv-bg-primary data-[state=active]:text-black rounded-full">Incoming ({incoming.length})</TabsTrigger>
          <TabsTrigger value="outgoing" data-testid="tab-outgoing" className="data-[state=active]:pv-bg-primary data-[state=active]:text-black rounded-full">Sent ({outgoing.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="suggested" className="mt-4">
          {!loading && !(user?.availability_slots?.length) && (
            <div data-testid="availability-nudge" className="card p-4 mb-4 flex items-center gap-3 border-l-4" style={{borderLeftColor:"#BED445"}}>
              <CalendarClock className="pv-text-dark shrink-0"/>
              <div className="flex-1 text-sm"><b className="pv-text-dark">Set your weekly availability</b> so we can rank players who are free when you are.</div>
              <Link to="/passport" data-testid="availability-nudge-link" className="btn-primary text-xs">Set times</Link>
            </div>
          )}
          {loading ? <Loader2 className="animate-spin"/> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {players.map((p) => (
                <div key={p.user_id} className="card p-4 rise-in" data-testid={`player-card-${p.user_id}`}>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 border-2 pv-border-primary">
                      <AvatarImage src={p.avatar}/><AvatarFallback>{p.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="font-bold pv-text-dark">{p.name}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12}/> {p.location}</div>
                    </div>
                    <div className="compat-ring" style={{"--p": p.compatibility}}><span>{p.compatibility}%</span></div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="pill pv-bg-primary text-black">DUPR {p.dupr_rating}</span>
                    <span className="pill" style={{background:"rgba(74,144,226,.15)", color:"#4A90E2"}}>{p.play_style}</span>
                    {p.streak >= 3 && <span className="streak-badge text-xs"><Flame size={12}/> {p.streak}</span>}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600">
                    <span className="line-clamp-1">{p.availability?.join(" · ")}</span>
                    <span data-testid={`shared-slots-${p.user_id}`} className={`pill shrink-0 flex items-center gap-1 ${p.shared_slots > 0 ? "pv-bg-deep-green text-white" : "bg-gray-100 text-gray-500"}`}>
                      <CalendarClock size={11}/> {p.shared_slots} shared {p.shared_slots === 1 ? "slot" : "slots"}
                    </span>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button data-testid={`challenge-btn-${p.user_id}`} onClick={()=>openChallenge(p)} className="btn-challenge flex-1 flex items-center justify-center gap-2 text-sm">
                      <Swords size={15}/> Challenge
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="incoming" className="mt-4 space-y-3">
          {incoming.length === 0 ? <EmptyState title="Inbox zero. Rare and glorious." /> : incoming.map((c) => (
            <div key={c.challenge_id} className="card p-4 flex items-center gap-3" data-testid={`incoming-${c.challenge_id}`}>
              <Avatar className="h-10 w-10"><AvatarImage src={c.challenger?.avatar}/><AvatarFallback>?</AvatarFallback></Avatar>
              <div className="flex-1">
                <div className="font-bold pv-text-dark">{c.challenger?.name} <span className="text-xs text-gray-500 font-normal">wants to play {c.match_type}</span></div>
                <div className="text-xs text-gray-500">{c.message}</div>
              </div>
              <button data-testid={`accept-${c.challenge_id}`} onClick={()=>respond(c.challenge_id,"accept")} className="btn-primary text-sm flex items-center gap-1"><Check size={14}/> Accept</button>
              <button data-testid={`decline-${c.challenge_id}`} onClick={()=>respond(c.challenge_id,"decline")} className="btn-ghost text-sm flex items-center gap-1"><X size={14}/> Decline</button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="outgoing" className="mt-4 space-y-3">
          {outgoing.length === 0 ? <EmptyState title="Send a challenge to get things rolling."/> : outgoing.map((c) => (
            <div key={c.challenge_id} className="card p-4 flex items-center gap-3" data-testid={`outgoing-${c.challenge_id}`}>
              <Avatar className="h-10 w-10"><AvatarImage src={c.opponent?.avatar}/><AvatarFallback>?</AvatarFallback></Avatar>
              <div className="flex-1">
                <div className="font-bold pv-text-dark">{c.opponent?.name}</div>
                <div className="text-xs text-gray-500">{c.match_type} · {c.message}</div>
              </div>
              <Badge variant="outline" className="capitalize">{c.status}</Badge>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!dialog} onOpenChange={(o)=>!o && setDialog(null)}>
        <DialogContent data-testid="challenge-dialog">
          <DialogHeader><DialogTitle>Challenge {dialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Avatar className="h-14 w-14 border-2 pv-border-primary"><AvatarImage src={dialog?.avatar}/><AvatarFallback>{dialog?.name?.[0]}</AvatarFallback></Avatar>
              <div>
                <div className="font-bold pv-text-dark">{dialog?.name}</div>
                <div className="text-xs text-gray-500">DUPR {dialog?.dupr_rating} · {dialog?.play_style}</div>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Proposed time (optional)</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Select value={day} onValueChange={setDay}>
                  <SelectTrigger data-testid="challenge-day-select"><SelectValue placeholder="Day"/></SelectTrigger>
                  <SelectContent>
                    {DAYS.map(d => <SelectItem key={d} value={d} data-testid={`challenge-day-${d}`}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="challenge-time-select"><SelectValue placeholder="Time"/></SelectTrigger>
                  <SelectContent>
                    {TIMES.map(t => <SelectItem key={t} value={t} data-testid={`challenge-time-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {day && time
                ? <div data-testid="challenge-time-preview" className="text-xs pv-text-dark mt-1 flex items-center gap-1"><CalendarClock size={12}/> Accepting drops <b>{day} {time}</b> onto both calendars.</div>
                : <div className="text-xs text-gray-400 mt-1">Leave blank to sort out the time later (TBD).</div>}
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Court (optional)</label>
              <Select value={courtId} onValueChange={setCourtId}>
                <SelectTrigger data-testid="challenge-court-select"><SelectValue placeholder="Pick a court"/></SelectTrigger>
                <SelectContent>
                  {courts.map(c => <SelectItem key={c.court_id} value={c.court_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Trash-talk (optional)</label>
              <textarea data-testid="challenge-message" value={msg} onChange={(e)=>setMsg(e.target.value)} className="w-full border rounded-xl p-3 text-sm mt-1" rows={3}/>
            </div>
            {mode === "doubles" && (
              <div className="grid grid-cols-1 gap-3 rounded-xl bg-[#F8F6F3] border border-black/5 p-3" data-testid="doubles-partner-picker">
                <div className="text-xs font-black uppercase tracking-wider text-gray-500">Lock in all four players</div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">Your partner</label>
                  <Select value={partnerId} onValueChange={setPartnerId}>
                    <SelectTrigger data-testid="my-partner-select"><SelectValue placeholder="Pick your partner"/></SelectTrigger>
                    <SelectContent>
                      {players.filter(p => p.user_id !== dialog?.user_id).map(p => (
                        <SelectItem key={p.user_id} value={p.user_id} data-testid={`my-partner-opt-${p.user_id}`}>{p.name} · {p.dupr_rating}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">{dialog?.name}'s partner</label>
                  <Select value={oppPartnerId} onValueChange={setOppPartnerId}>
                    <SelectTrigger data-testid="opp-partner-select"><SelectValue placeholder="Pick their partner"/></SelectTrigger>
                    <SelectContent>
                      {players.filter(p => p.user_id !== dialog?.user_id && p.user_id !== partnerId).map(p => (
                        <SelectItem key={p.user_id} value={p.user_id} data-testid={`opp-partner-opt-${p.user_id}`}>{p.name} · {p.dupr_rating}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-gray-500">You &amp; <b>{players.find(p=>p.user_id===partnerId)?.name || "—"}</b> vs {dialog?.name} &amp; <b>{players.find(p=>p.user_id===oppPartnerId)?.name || "—"}</b></div>
              </div>
            )}
            <div className="text-xs text-gray-500">Match type: <b className="capitalize">{mode}</b></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setDialog(null)}>Cancel</Button>
            <button data-testid="send-challenge-btn" onClick={send} className="btn-challenge">Send Challenge</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ title }) {
  return <div className="card p-8 text-center text-gray-500 text-sm">{title}</div>;
}
